import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";

export default function Financeiro() {
  const { t, fmtCurrency } = useT();
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState([]);
  const [maint, setMaint] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/dashboard/stats"), api.get("/payments"), api.get("/maintenance")])
      .then(([s, p, m]) => { setStats(s.data); setPayments(p.data); setMaint(m.data); });
  }, []);

  if (!stats) return <div className="overline">{t("common.loading")}</div>;

  const revByCar = {};
  for (const p of payments) {
    if (p.status !== "pago" || !p.rental) continue;
    revByCar[p.rental.car_id] = (revByCar[p.rental.car_id] || 0) + Number(p.amount);
  }
  const costByCar = {};
  for (const m of maint) {
    costByCar[m.car_id] = (costByCar[m.car_id] || 0) + Number(m.cost);
  }
  const byCarRows = Object.keys({ ...revByCar, ...costByCar }).map((cid) => {
    const sample = payments.find((p) => p.rental?.car_id === cid)?.rental?.car
                || maint.find((m) => m.car_id === cid)?.car;
    return {
      car_id: cid,
      label: sample ? `${sample.model} · ${sample.plate}` : cid.slice(0, 8),
      revenue: revByCar[cid] || 0,
      cost: costByCar[cid] || 0,
      profit: (revByCar[cid] || 0) - (costByCar[cid] || 0),
    };
  }).sort((a, b) => b.revenue - a.revenue);

  return (
    <div className="space-y-8" data-testid="financeiro-page">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border-l border-t border-zinc-200">
        <Stat label={t("financial.revenueTotal")} value={fmtCurrency(stats.revenue.total)} />
        <Stat label={t("financial.revenueMonth")} value={fmtCurrency(stats.revenue.month)} />
        <Stat label={t("financial.expenses")} value={fmtCurrency(stats.revenue.expenses)} tone="alert" />
        <Stat label={t("financial.profit")} value={fmtCurrency(stats.revenue.profit)} tone={stats.revenue.profit >= 0 ? "success" : "alert"} />
      </div>

      <div className="border border-zinc-200 bg-white p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="overline">{t("financial.monthlyRevenue")}</div>
            <h3 className="font-display font-bold text-xl tracking-tight">{t("financial.last6")}</h3>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats.revenue.by_month}>
              <CartesianGrid stroke="#E4E4E7" vertical={false} />
              <XAxis dataKey="month" stroke="#71717A" fontSize={11} tickLine={false} />
              <YAxis stroke="#71717A" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                cursor={{ fill: "#F4F4F5" }}
                contentStyle={{ background: "#0A0A0A", border: "none", color: "#fff", borderRadius: 0, fontSize: 12 }}
                formatter={(v) => [fmtCurrency(v), t("financial.revenue")]}
              />
              <Bar dataKey="revenue" fill="#F5A623" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="border border-zinc-200 bg-white">
        <div className="p-6 border-b border-zinc-200">
          <div className="overline">{t("financial.perVehicle")}</div>
          <h3 className="font-display font-bold text-xl tracking-tight">{t("financial.revenueVsCost")}</h3>
        </div>
        {byCarRows.length === 0 ? (
          <div className="p-16 text-center overline">{t("dashboard.noData")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">
                <Th>{t("financial.vehicle")}</Th><Th>{t("financial.revenue")}</Th><Th>{t("financial.costs")}</Th><Th>{t("financial.profit")}</Th>
              </tr>
            </thead>
            <tbody>
              {byCarRows.map((r) => (
                <tr key={r.car_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <Td className="font-medium">{r.label}</Td>
                  <Td className="font-mono text-green-700 font-semibold">{fmtCurrency(r.revenue)}</Td>
                  <Td className="font-mono text-red-700">{fmtCurrency(r.cost)}</Td>
                  <Td className={`font-mono font-semibold ${r.profit >= 0 ? "text-zinc-900" : "text-red-700"}`}>{fmtCurrency(r.profit)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "default" }) {
  const tones = { default: "text-zinc-900", success: "text-green-700", alert: "text-red-700" };
  return (
    <div className="border-r border-b border-zinc-200 -ml-px -mt-px p-6 bg-white">
      <div className="overline mb-3">{label}</div>
      <div className={`font-display font-black text-3xl tracking-tighter ${tones[tone]}`}>{value}</div>
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 overline">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
